import { useEffect, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { Minus, Plus } from 'lucide-react'

import type { SliderBound } from '../../core/capacityEnvelope'
import { clampSliderValue, coresNumber, formatCores, parseCores, parseWholeGb } from '../../core/sizing'

/**
 * `free` is the slider bound (floored to the step), so the control and the
 * meter agree on what can be requested. `used` comes from the raw reading, so
 * the floor remainder is never attributed to other tenants.
 */
export type ResourceMeter = { requested: number; free: number; used: number; total: number }

type ResourceId = 'cpu' | 'ram' | 'storage'

const CAP_NOTE_MS = 3000

const AXIS_WORD: Record<ResourceId, string> = { cpu: 'CPU', ram: 'memory', storage: 'storage' }

function displayValue(id: ResourceId, value: number): string {
  return id === 'cpu' ? coresNumber(value) : value.toLocaleString('en-US')
}

function withUnit(id: ResourceId, value: number): string {
  return id === 'cpu' ? formatCores(value) : `${value.toLocaleString('en-US')} GB`
}

/** The meter is only drawn from numbers that describe one host consistently. */
export function meterIsReadable(meter: ResourceMeter | undefined): meter is ResourceMeter {
  return meter !== undefined && meter.total > 0 && meter.free >= 0 && meter.free <= meter.total && meter.used >= 0 && meter.used <= meter.total
}

function percent(part: number, total: number): string {
  return `${total > 0 ? (Math.max(0, Math.min(part, total)) / total) * 100 : 0}%`
}

/**
 * One Size row: label and what is left on the left, one track in the middle,
 * a typed field with −/+ steppers on the right.
 *
 * The track is drawn to the host's TOTAL capacity: your request fills from the
 * left, capacity in use by others is hatched at the right end, and the empty
 * stretch between is free. The native range (kept for keyboard and screen
 * reader input, and read by id in the e2e suite) is laid over the free part
 * only, so its maximum is what is free and its thumb sits on the fill's edge.
 *
 * The typed field is a text input on purpose (a scroll over `type="number"`
 * silently changes it) and commits on blur or Enter, never per keystroke, so a
 * half-typed "1." does not clamp under the tenant's fingers.
 *
 * `unset` is a row the tenant has not chosen yet: the field is empty, the
 * range rests at its minimum without claiming it, and nothing is requested.
 * `locked` is the whole host: full, read-only, no thumb.
 */
export function ResourceControl({ id, label, value, bound, meter, capIsFree = false, disabled = false, locked = false, unset = false, error, onChange }: {
  id: ResourceId
  label: 'CPU' | 'Memory' | 'Storage'
  value: number
  bound: SliderBound
  meter?: ResourceMeter | undefined
  /** The bound is this host's free capacity, not a network or default limit. */
  capIsFree?: boolean
  disabled?: boolean
  /** The whole host is rented: the size is the host's, not the tenant's. */
  locked?: boolean
  unset?: boolean
  /** Shown under the field, e.g. an unset row after submit. */
  error?: string | undefined
  onChange: (value: number) => void
}) {
  const shown = (next: number) => unset ? '' : displayValue(id, next)
  const [text, setText] = useState(() => shown(value))
  const [invalid, setInvalid] = useState(false)
  const [capNote, setCapNote] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const unit = id === 'cpu' ? 'cores' : 'GB'
  const fieldId = `${id}-value`
  const errorId = `${fieldId}-error`
  const requiredId = `${fieldId}-required`
  const noteId = `${fieldId}-note`
  const inert = disabled || locked

  // A slider drag, stepper or size card changes the value from outside the field.
  useEffect(() => { setText(unset ? '' : displayValue(id, value)) }, [id, value, unset])

  useEffect(() => {
    if (!capNote) return
    const timer = window.setTimeout(() => setCapNote(null), CAP_NOTE_MS)
    return () => window.clearTimeout(timer)
  }, [capNote])

  const commit = (fromBlur: boolean) => {
    if (inert) return
    const parsed = id === 'cpu' ? parseCores(text) : parseWholeGb(text.replace(/,/g, ''))
    if (parsed === null) {
      if (fromBlur) {
        // Nothing was committed; show the value the draft still holds.
        setText(shown(value))
        setInvalid(false)
      } else {
        setInvalid(true)
      }
      return
    }
    const clamped = clampSliderValue(parsed, bound)
    setInvalid(false)
    setText(displayValue(id, clamped))
    setCapNote(parsed > bound.max ? `Capped at ${withUnit(id, bound.max)}${capIsFree ? ' free' : ' maximum'}` : null)
    if (clamped !== value || unset) onChange(clamped)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    // Enter commits the field; it must not submit the whole configure form.
    event.preventDefault()
    commit(false)
  }

  const step = (direction: 1 | -1) => {
    const from = unset ? bound.min - direction * bound.step : value
    const next = clampSliderValue(from + direction * bound.step, bound)
    setCapNote(null)
    if (next !== value || unset) onChange(next)
  }

  const readable = meterIsReadable(meter)
  const requested = unset ? 0 : Math.min(value, bound.max)
  const left = readable ? Math.max(0, meter.free - requested) : null
  const hint = unset
    ? 'Not set'
    : readable && left !== null
      ? `${displayValue(id, left)} of ${withUnit(id, meter.free)} left`
      : null
  const valueText = unset
    ? 'Not set'
    : readable && left !== null ? `${withUnit(id, value)}, ${displayValue(id, left)} of ${displayValue(id, meter.free)} left` : withUnit(id, value)

  // Without a host meter the track is the bound itself.
  const total = readable ? meter.total : bound.max
  const origin = readable ? 0 : bound.min
  const span = Math.max(0, total - origin)
  const trackStyle = {
    '--track-fill': percent(requested - origin, span),
    '--track-used': readable ? percent(meter.used, meter.total) : '0%',
    // The range covers min…free of the track, so its thumb lands on the fill's edge.
    '--track-input-left': readable ? percent(bound.min, meter.total) : '0%',
    '--track-input-width': readable ? percent(Math.max(0, bound.max - bound.min), meter.total) : '100%',
  } as CSSProperties
  const describedBy = [invalid ? errorId : null, error ? requiredId : null, capNote ? noteId : null].filter(Boolean).join(' ') || undefined

  return <div className={['resource-row', unset && 'resource-row-unset', locked && 'resource-row-locked'].filter(Boolean).join(' ')}>
    <div className="resource-label">
      <label htmlFor={id}>{label}</label>
      {hint && <span className="resource-secondary">{hint}</span>}
    </div>
    <div className="resource-track" style={trackStyle} data-dragging={dragging || undefined}>
      <div className="size-track" aria-hidden="true">
        <span className="size-track-fill" />
        {readable && meter.used > 0 && <span className="size-track-used" />}
      </div>
      <input id={id} type="range" min={bound.min} max={bound.max} step={bound.step} value={value} disabled={inert}
        aria-valuetext={valueText} aria-invalid={error ? true : undefined} aria-describedby={error ? requiredId : undefined}
        onPointerDown={() => setDragging(true)} onPointerUp={() => setDragging(false)} onPointerCancel={() => setDragging(false)} onBlur={() => setDragging(false)}
        onChange={(event) => onChange(clampSliderValue(Number(event.target.value), bound))} onClick={() => { if (unset) onChange(value) }} />
    </div>
    <div className="resource-value">
      <span className="resource-value-field">
        <button type="button" className="resource-step" aria-label={`Decrease ${AXIS_WORD[id]}`} disabled={inert || (!unset && value <= bound.min)} onClick={() => step(-1)}><Minus aria-hidden="true" size={14} /></button>
        <input id={fieldId} type="text" inputMode={id === 'cpu' ? 'decimal' : 'numeric'} autoComplete="off" aria-label={`${label} in ${unit}`} aria-invalid={invalid || Boolean(error) || undefined} aria-describedby={describedBy} value={text} placeholder={unset ? 'Choose' : undefined} disabled={disabled} readOnly={locked}
          title={id === 'cpu' && !unset ? `${value.toLocaleString('en-US')} millicores` : undefined}
          onChange={(event) => { setText(event.target.value); setInvalid(false); setCapNote(null) }}
          onBlur={() => commit(true)}
          onKeyDown={onKeyDown} />
        <button type="button" className="resource-step" aria-label={`Increase ${AXIS_WORD[id]}`} disabled={inert || (!unset && value >= bound.max)} onClick={() => step(1)}><Plus aria-hidden="true" size={14} /></button>
        <span className="resource-unit" aria-hidden="true">{unit}</span>
      </span>
      {invalid && <span className="field-error" id={errorId}>Enter a number</span>}
      {error && !invalid && <span className="field-error" id={requiredId}>{error}</span>}
      {capNote && <span className="field-help" id={noteId} role="status">{capNote}</span>}
    </div>
  </div>
}
